import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  useTheme,
  alpha,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AppsIcon from '@mui/icons-material/Apps';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { formatRelative } from 'date-fns';

import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import { EntityType } from '@/components/entities/entityType';

type Props = {
  pipeline: any;
  maxHeight?: number;
  onSave: (updated: { name: string; description: string }) => Promise<void>;
  onCancel?: () => void;
};

const PipelineConfiguration = ({ pipeline, maxHeight, onSave, onCancel }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [name, setName] = useState<string>(pipeline?.name || '');
  const [description, setDescription] = useState<string>(pipeline?.description || '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [nameError, setNameError] = useState<string>('');

  useEffect(() => {
    if (pipeline) {
      setName(pipeline.name || '');
      setDescription(pipeline.description || '');
      setNameError('');
    }
  }, [pipeline?.id, pipeline?.name, pipeline?.description]);

  const hasChanged =
    name.trim() !== (pipeline?.name || '').trim() ||
    description.trim() !== (pipeline?.description || '').trim();

  const handleNameChange = (val: string) => {
    setName(val);
    if (!val.trim()) {
      setNameError(t('common.errors.requiredField', 'Required field'));
    } else {
      setNameError('');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(t('common.errors.requiredField', 'Required field'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setName(pipeline?.name || '');
    setDescription(pipeline?.description || '');
    setNameError('');
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  const items = pipeline?.items || [];
  const dataSources = items.filter((item: any) => item.entityType === EntityType.dataSourceInstance);
  const agents = items.filter((item: any) => item.entityType === EntityType.agentInstance);
  const userApps = items.filter((item: any) => item.entityType === EntityType.userAppInstance);

  return (
    <Box
      sx={{
        height: maxHeight ? `${maxHeight}px` : '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 2.5,
      }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
        <Stack spacing={2.5}>
          {error && <ErrorMessage error={error} onClose={() => setError(null)} />}

          {/* General Pipeline Information Card */}
          <Card
            className="glass-card-light"
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    p: 1,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SettingsOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
                </Box>
              }
              title={
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {t('entity.instance.tabs.configuration', 'Pipeline Configuration')}
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  {t('Configure identity, metadata, and properties for this pipeline instance')}
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ opacity: 0.6 }} />
            <CardContent sx={{ pt: 2.5 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('entity.instance.fields.name', 'Pipeline Name')}
                    value={name}
                    required
                    error={nameError}
                    onChange={handleNameChange}
                    placeholder="Enter pipeline name"
                    disabled={saving}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('entity.instance.fields.description', 'Description')}
                    value={description}
                    multiline
                    rows={3}
                    onChange={(val) => setDescription(val)}
                    placeholder="Describe the purpose and data flow of this pipeline"
                    disabled={saving}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Pipeline Metadata & Lineage Card */}
          <Card
            className="glass-card-light"
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    p: 1,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountTreeIcon color="secondary" sx={{ fontSize: 20 }} />
                </Box>
              }
              title={
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {t('Pipeline Metadata & Lineage')}
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  {t('Underlying blueprint, timestamps, and tenant attributes')}
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ opacity: 0.6 }} />
            <CardContent sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                    Pipeline ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                    {pipeline?.id || '—'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                    Blueprint Lineage
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {pipeline?.blueprintId ? pipeline.blueprintId : 'Custom Pipeline'}
                    </Typography>
                    {pipeline?.isSample && (
                      <Chip
                        icon={<AutoAwesomeIcon sx={{ fontSize: '11px !important', color: '#b45309 !important' }} />}
                        label="Sample"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '10px',
                          fontWeight: 750,
                          bgcolor: '#fef3c7',
                          color: '#b45309',
                          border: '1px solid #fde68a',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                    Created
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {pipeline?.createdDate ? formatRelative(new Date(pipeline.createdDate), new Date()) : '—'}
                    </Typography>
                  </Stack>
                  {pipeline?.createdBy && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <PersonOutlineIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        by {pipeline.createdBy}
                      </Typography>
                    </Stack>
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                    Last Updated
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {pipeline?.lastUpdatedDate ? formatRelative(new Date(pipeline.lastUpdatedDate), new Date()) : '—'}
                    </Typography>
                  </Stack>
                  {pipeline?.lastUpdatedBy && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <PersonOutlineIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        by {pipeline.lastUpdatedBy}
                      </Typography>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Connected Components Topology Overview */}
          <Card
            className="glass-card-light"
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    p: 1,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <StorageIcon color="info" sx={{ fontSize: 20 }} />
                </Box>
              }
              title={
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {t('Configured Pipeline Components')}
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  {t('Active entities connected and executing within this pipeline instance')}
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ opacity: 0.6 }} />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  icon={<StorageIcon sx={{ fontSize: '13px !important', color: '#047857 !important' }} />}
                  label={`${dataSources.length} Data Source${dataSources.length !== 1 ? 's' : ''}`}
                  sx={{ bgcolor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontWeight: 700 }}
                />
                <Chip
                  icon={<PsychologyIcon sx={{ fontSize: '13px !important', color: '#6d28d9 !important' }} />}
                  label={`${agents.length} Agent${agents.length !== 1 ? 's' : ''}`}
                  sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', fontWeight: 700 }}
                />
                <Chip
                  icon={<AppsIcon sx={{ fontSize: '13px !important', color: '#0369a1 !important' }} />}
                  label={`${userApps.length} App${userApps.length !== 1 ? 's' : ''}`}
                  sx={{ bgcolor: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: 700 }}
                />
              </Stack>

              <Grid container spacing={2}>
                {/* Data Sources */}
                {dataSources.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.01)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Data Sources
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {dataSources.map((ds: any) => (
                          <Box key={ds.id} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {ds.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ds.dataSourceDef?.name || ds.entityDefId}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                )}

                {/* Agents */}
                {agents.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.01)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Agents
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {agents.map((ag: any) => (
                          <Box key={ag.id} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {ag.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ag.agentDef?.name || ag.entityDefId}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                )}

                {/* Apps */}
                {userApps.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.01)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        User Apps
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {userApps.map((app: any) => (
                          <Box key={app.id} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {app.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {app.userAppDef?.name || app.entityDefId}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Action Footer Bar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRadius: '12px',
          mt: 2,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.02)',
        }}
      >
        <Button
          size="small"
          variant="text"
          onClick={handleReset}
          disabled={saving || !hasChanged}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('common.buttons.reset', 'Reset')}
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasChanged}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            px: 2.5,
            color: 'text.primary',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.buttons.saveChanges', 'Save Changes')}
        </Button>
      </Box>
    </Box>
  );
};

export default PipelineConfiguration;
